import {
    BankingGeneralDisclosure,
    CashApyDisclosure,
    CashBackDisclosure,
    InvestmentManagementAgreementDisclosure,
} from '@fruitful/compliance';
import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    MembershipBillingFrequency,
    type StripeSubscriptionSummary,
    type SubscriptionProduct,
    SubscriptionProductId,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { differenceInCalendarDays } from 'date-fns';
import { Link } from 'expo-router';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { Platform } from 'react-native';
import {
    BadgeLabel,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CalendarIcon,
    CheckCircleIcon,
    CircleCheckIcon,
    CirclePlusIcon,
    type ColorTokenKey,
    FootnoteEmphasized,
    FootnoteRegular,
    FruitfulLogoIcon,
    H5Regular,
    H6Emphasized,
    Icon,
    type IconType,
    isNonMobileWeb,
    LabelEmphasized,
    LabelRegular,
    NavigationRow,
    ScreenContainer,
    Sheet,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import Logo from '../../../assets/images/logos/fruitful-logo-icon-only.svg';
import { INVESTMENT_NOT_FDIC_INSURED } from '../../banking/components/Disclosures';
import { useGetSubscriptionProductCatalog } from '../../billing/hooks/useSubscriptionProductCatalog';
import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { centsToDollars, formatDollars } from '../../common/utils/bankingUtils';
import {
    convertToDate,
    format,
    formatReadableMonthDay,
    SHORT_READABLE_DATE_FORMAT,
} from '../../common/utils/dateUtils';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import useGetSubscription from '../hooks/useGetSubscription';
import { SUPPORT_EMAIL } from '../types/constants';
import {
    formatAumFeeTileValue,
    getLegacySubscriptionBenefits,
    getMembershipPerks,
    getMemberSinceDate,
    getSubscriptionBenefits,
    TRIAL_UPGRADE_PERKS,
} from '../types/SubscriptionSummary';
import { AutoRenewToggleSheet } from './AutoRenewToggleSheet';
import { PremiumUpgradeComparisonSheet } from './PremiumUpgradeComparisonSheet';

const SubscriptionSettingsScreen = () => {
    const user = useAuthedUser();
    const { data: subscription, isLoading } = useGetSubscription({
        userId: user.id,
    });

    return (
        <ScreenContainer title="Your membership" hasBackButton={true}>
            {isLoading ? (
                <Spinner />
            ) : (
                <SubscriptionDetails subscription={subscription} />
            )}
        </ScreenContainer>
    );
};

const getDaysLeftInTrial = (
    trialEndDate: string | null | undefined,
): number | null => {
    if (trialEndDate == null) {
        return null;
    }
    return Math.max(
        0,
        differenceInCalendarDays(convertToDate(trialEndDate), new Date()),
    );
};

const getPremiumTrialEndDate = (
    subscription: StripeSubscriptionSummary,
): string | null | undefined =>
    subscription.type !== SubscriptionProductId.TRIAL_PREMIUM
        ? null
        : (subscription.currentPhase?.endDate ??
          subscription.nextPhase?.startDate ??
          null);

const isSwitchingToStandard = (
    subscription: StripeSubscriptionSummary,
): boolean =>
    subscription.nextPhase?.product?.id === SubscriptionProductId.STANDARD;

const getPremiumRenewalChipLabel = ({
    isActualPremium,
    isBillingSettingsEnabled,
    isAutoRenewOff,
    pendingSwitchDate,
    subscription,
}: {
    isActualPremium: boolean;
    isBillingSettingsEnabled: boolean;
    isAutoRenewOff: boolean;
    pendingSwitchDate: string | null;
    subscription: StripeSubscriptionSummary;
}): string | null => {
    if (!isActualPremium || !isBillingSettingsEnabled) {
        return null;
    }
    if (isAutoRenewOff) {
        return pendingSwitchDate != null
            ? `Ends on ${format(pendingSwitchDate, SHORT_READABLE_DATE_FORMAT)}`
            : null;
    }
    return subscription.membershipYearEndsAt != null
        ? `Renews ${format(subscription.membershipYearEndsAt, SHORT_READABLE_DATE_FORMAT)}`
        : null;
};

const SubscriptionDetailsCurrent = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    const user = useAuthedUser();
    const { value: isBillingSettingsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );
    const [isCancelMembershipSheetOpen, setIsCancelMembershipSheetOpen] =
        useState<boolean>(false);
    const [isComparisonSheetOpen, setIsComparisonSheetOpen] =
        useState<boolean>(false);

    const product = subscription.product;
    if (product == null) {
        return <NoMatchingSubscription />;
    }

    const isStandard = product.id === SubscriptionProductId.STANDARD;
    const displayName = isStandard ? 'Fruitful member' : product.displayName;
    const memberSinceDate = isStandard ? getMemberSinceDate(user) : null;

    const trialEndDate = getPremiumTrialEndDate(subscription);
    const daysLeftInTrial = getDaysLeftInTrial(trialEndDate);

    const isPeach = product.id !== SubscriptionProductId.STANDARD;
    const backgroundColor = isPeach ? '$peachLight' : '$gray200';
    const textColor = isPeach ? '$peachDarkest' : '$gray1200';

    const isOnPremiumTrial = product.id === SubscriptionProductId.TRIAL_PREMIUM;
    const nextProduct = subscription.nextPhase?.product;
    const isChangingProduct =
        nextProduct != null && nextProduct.id !== product.id;
    const isNextProductStandard =
        isChangingProduct && isSwitchingToStandard(subscription);
    const isNextProductPremium =
        isChangingProduct && nextProduct.id === SubscriptionProductId.PREMIUM;

    const isActualPremium = product.id === SubscriptionProductId.PREMIUM;
    // Trials always roll into Standard by default, so only paid Premium can
    // have auto-renew "off".
    const isAutoRenewOff =
        isActualPremium && isBillingSettingsEnabled && isNextProductStandard;
    const pendingSwitchDate = subscription.nextPhase?.startDate ?? null;
    const premiumRenewalChipLabel = getPremiumRenewalChipLabel({
        isActualPremium,
        isBillingSettingsEnabled,
        isAutoRenewOff,
        pendingSwitchDate,
        subscription,
    });
    const showTrialUpgradeOffer = isOnPremiumTrial && !isNextProductPremium;

    // TODO: this is a gross hack and the real values need to be pulled from backend
    const premiumDisplayName = 'Premium';
    const premiumSubscriptionBenefits = getSubscriptionBenefits({
        ...product,
        id: SubscriptionProductId.PREMIUM,
        displayName: premiumDisplayName,
    });

    const nextStandardSectionTitle = isOnPremiumTrial
        ? 'When your trial ends'
        : `Switching to ${nextProduct?.displayName ?? 'Standard'}`;
    const nextPremiumSectionTitle = isOnPremiumTrial
        ? `After your trial, you've got ${premiumDisplayName}`
        : `Upgrading to ${premiumDisplayName}`;

    return (
        <>
            <YStack gap={24} width="100%">
                <YStack
                    gap={24}
                    padding={isPeach ? 24 : 20}
                    borderRadius={24}
                    backgroundColor={backgroundColor}
                    justifyContent="center"
                >
                    <YStack gap={16} width="100%">
                        <YStack gap={4} width="100%">
                            <XStack
                                alignItems="center"
                                justifyContent={
                                    isActualPremium
                                        ? 'flex-start'
                                        : 'space-between'
                                }
                                flexWrap="wrap"
                                gap={8}
                                width="100%"
                            >
                                <H5Regular color={textColor}>
                                    {displayName}
                                </H5Regular>
                                {memberSinceDate != null && (
                                    <BadgeLabel
                                        variant="neutral-alt"
                                        size="regular"
                                        text={`Since ${format(memberSinceDate, SHORT_READABLE_DATE_FORMAT)}`}
                                    />
                                )}
                                {daysLeftInTrial != null && (
                                    <XStack
                                        backgroundColor="$peach"
                                        paddingHorizontal={12}
                                        paddingVertical={4}
                                        borderRadius={16}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <LabelRegular
                                            color="$peachDarkest"
                                            textAlign="right"
                                        >
                                            {daysLeftInTrial === 1
                                                ? '1 day left in trial'
                                                : `${daysLeftInTrial} days left in trial`}
                                        </LabelRegular>
                                    </XStack>
                                )}
                                {premiumRenewalChipLabel != null && (
                                    <XStack
                                        backgroundColor="$peach"
                                        paddingHorizontal={12}
                                        paddingVertical={4}
                                        borderRadius={16}
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <LabelRegular
                                            color="$peachDarkest"
                                            textAlign="right"
                                        >
                                            {premiumRenewalChipLabel}
                                        </LabelRegular>
                                    </XStack>
                                )}
                            </XStack>
                            {trialEndDate != null && (
                                <BodySmallRegular color="$peachDarkest">
                                    Your trial ends on{' '}
                                    {format(trialEndDate, 'MMMM d')}.
                                </BodySmallRegular>
                            )}
                        </YStack>
                        {isActualPremium && (
                            <AnnualMembershipSummary
                                subscription={subscription}
                            />
                        )}
                        <MembershipStatGrid
                            product={product}
                            textColor={textColor}
                            tileColor={isPeach ? '$peachLightest' : '$white'}
                        />
                        <YStack gap={isOnPremiumTrial ? 12 : 0} width="100%">
                            {getMembershipPerks(product.id).map((perk) => (
                                <SubscriptionBenefitRow
                                    key={perk}
                                    benefit={perk}
                                    textColor={textColor}
                                    backgroundColor={backgroundColor}
                                    icon={CircleCheckIcon}
                                />
                            ))}
                        </YStack>
                    </YStack>
                    {showTrialUpgradeOffer && (
                        <TrialUpgradeOffer
                            onPressUpgrade={() => {
                                setIsComparisonSheetOpen(true);
                            }}
                        />
                    )}
                </YStack>

                {isActualPremium && (
                    <AutoRenewCtaAndSheet
                        userId={user.id}
                        subscription={subscription}
                        copyVariant="premium"
                        allowRevert={true}
                    />
                )}

                {isStandard && (
                    <UpgradeToPremiumWidget
                        onPressUpgrade={() => {
                            setIsComparisonSheetOpen(true);
                        }}
                    />
                )}

                {/* Premium with auto-renew off is communicated by the "Ends on" chip instead. */}
                {isNextProductStandard && !isAutoRenewOff && (
                    <YStack
                        gap={16}
                        padding={20}
                        borderRadius={24}
                        backgroundColor="$gray200"
                        justifyContent="center"
                    >
                        <H5Regular color="$gray1200">
                            {nextStandardSectionTitle}
                        </H5Regular>
                        <MembershipStatGrid
                            product={nextProduct}
                            textColor="$gray1200"
                            tileColor="$white"
                        />
                        <YStack width="100%">
                            {getMembershipPerks(nextProduct.id).map((perk) => (
                                <SubscriptionBenefitRow
                                    key={perk}
                                    benefit={perk}
                                    textColor="$gray1200"
                                    backgroundColor="$gray200"
                                    icon={CircleCheckIcon}
                                />
                            ))}
                        </YStack>
                    </YStack>
                )}

                {isNextProductPremium && (
                    <YStack
                        gap={24}
                        padding={20}
                        borderRadius={24}
                        backgroundColor="$peachLight"
                        justifyContent="center"
                    >
                        <YStack gap={4} width="100%">
                            <H5Regular>{nextPremiumSectionTitle}</H5Regular>
                        </YStack>
                        <YStack gap={12} width="100%">
                            {premiumSubscriptionBenefits?.map(
                                (benefit, index) => (
                                    <SubscriptionBenefitRow
                                        key={index}
                                        benefit={benefit}
                                        textColor="$gray1200"
                                        backgroundColor="$peachLight"
                                        icon={CheckCircleIcon}
                                    />
                                ),
                            )}
                        </YStack>
                    </YStack>
                )}

                {!isBillingSettingsEnabled && (
                    <BodyMediumRegular
                        color="$gray1000"
                        onPress={() => {
                            setIsCancelMembershipSheetOpen(true);
                        }}
                        cursor="pointer"
                        textDecorationLine="underline"
                    >
                        How can I change or cancel my membership?
                    </BodyMediumRegular>
                )}
            </YStack>
            <CancelMembershipSheet
                isOpen={isCancelMembershipSheetOpen}
                onClose={() => {
                    setIsCancelMembershipSheetOpen(false);
                }}
            />
            <PremiumUpgradeComparisonSheet
                isOpen={isComparisonSheetOpen}
                onClose={() => {
                    setIsComparisonSheetOpen(false);
                }}
            />
            <YStack
                paddingTop={24}
                paddingBottom={Platform.OS === 'web' ? 16 : 40}
            >
                <BenefitsDisclosures />
            </YStack>
        </>
    );
};

const SubscriptionDetailsPlusEssential = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    const user = useAuthedUser();
    const { value: isBillingSettingsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );
    const [isCancelMembershipSheetOpen, setIsCancelMembershipSheetOpen] =
        useState<boolean>(false);

    const color =
        subscription.type === SubscriptionProductId.ESSENTIAL
            ? 'peach'
            : 'green';
    const backgroundColor = color === 'peach' ? '$peach200' : '$green200';
    const textColor = color === 'peach' ? '$peach1200' : '$green1200';

    const trialEndDate = subscription.trialEndsDate;
    const daysLeftInTrial = getDaysLeftInTrial(trialEndDate);

    const benefits =
        subscription.product != null
            ? getSubscriptionBenefits(subscription.product)
            : getLegacySubscriptionBenefits(subscription.type);

    const pendingSwitchDate = subscription.nextPhase?.startDate ?? null;
    const isAutoRenewOff =
        isBillingSettingsEnabled && isSwitchingToStandard(subscription);

    return (
        <>
            <YStack
                gap={12}
                padding={24}
                borderRadius={16}
                backgroundColor={backgroundColor}
            >
                <YStack>
                    <XStack
                        gap={8}
                        alignItems="center"
                        width="100%"
                        justifyContent="space-between"
                    >
                        <H5Regular color={textColor}>
                            {capitalize(subscription.type ?? '')}
                        </H5Regular>
                        {daysLeftInTrial != null && daysLeftInTrial > 0 && (
                            <XStack
                                backgroundColor="$peach"
                                paddingHorizontal={12}
                                paddingVertical={4}
                                borderRadius={16}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <LabelRegular
                                    color="$peachDarkest"
                                    textAlign="right"
                                >
                                    {daysLeftInTrial === 1
                                        ? '1 day left in trial'
                                        : `${daysLeftInTrial} days left in trial`}
                                </LabelRegular>
                            </XStack>
                        )}
                    </XStack>
                    {subscription.cancelAtPeriodEnd &&
                        subscription.cancelAtDate != null && (
                            <BodySmallRegular color={textColor}>
                                Ends on{' '}
                                {formatReadableMonthDay(
                                    subscription.cancelAtDate,
                                )}{' '}
                            </BodySmallRegular>
                        )}
                    {!subscription.cancelAtPeriodEnd &&
                        isAutoRenewOff &&
                        pendingSwitchDate != null && (
                            <BodySmallRegular color={textColor}>
                                Ends on{' '}
                                {formatReadableMonthDay(pendingSwitchDate)}{' '}
                            </BodySmallRegular>
                        )}
                    {subscription.nextInvoiceAmountDueCents != null && (
                        <BodySmallRegular color={textColor}>
                            Renews on{' '}
                            {formatReadableMonthDay(subscription.renewalDate)}{' '}
                            for{' '}
                            {formatDollars(
                                centsToDollars(
                                    subscription.nextInvoiceAmountDueCents,
                                ),
                                false,
                            )}
                        </BodySmallRegular>
                    )}
                </YStack>
                <YStack>
                    {benefits.map((benefit, index) => (
                        <SubscriptionBenefitRowLegacy
                            key={index}
                            benefit={benefit}
                            textColor={textColor}
                        />
                    ))}
                </YStack>
                <BodyMediumRegular
                    color={textColor}
                    onPress={() => {
                        setIsCancelMembershipSheetOpen(true);
                    }}
                    cursor="pointer"
                    textDecorationLine="underline"
                >
                    How can I cancel my membership?
                </BodyMediumRegular>
            </YStack>
            <YStack marginTop={16} width="100%">
                <AutoRenewCtaAndSheet
                    userId={user.id}
                    subscription={subscription}
                    copyVariant="legacy"
                    allowRevert={false}
                />
            </YStack>
            <CancelMembershipSheet
                isOpen={isCancelMembershipSheetOpen}
                onClose={() => {
                    setIsCancelMembershipSheetOpen(false);
                }}
            />
            <BenefitsDisclosuresLegacy />
        </>
    );
};

const UpgradeToPremiumWidget = ({
    onPressUpgrade,
}: {
    onPressUpgrade: () => void;
}) => {
    const { value: isPremiumMembershipsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_PREMIUM_MEMBERSHIPS,
    );
    const { data: catalog } = useGetSubscriptionProductCatalog({
        subscriptionProductIds: [SubscriptionProductId.PREMIUM],
    });
    const premium = catalog?.[SubscriptionProductId.PREMIUM];

    if (!isPremiumMembershipsEnabled) {
        return null;
    }

    return (
        <YStack
            gap={24}
            padding={24}
            borderRadius={24}
            backgroundColor="$peachLight"
        >
            <YStack gap={16} width="100%">
                <YStack gap={2} width="100%">
                    <H5Regular color="$peachDarkest">
                        Upgrade to Premium
                    </H5Regular>
                    <BodySmallRegular color="$peachDarkest">
                        Dedicated guidance to reach your goals, plus better
                        rates.
                    </BodySmallRegular>
                </YStack>
                {premium != null && (
                    <MembershipStatGrid
                        product={premium}
                        textColor="$peachDarkest"
                        tileColor="$peachLightest"
                    />
                )}
                <YStack width="100%">
                    {getMembershipPerks(SubscriptionProductId.PREMIUM).map(
                        (perk) => (
                            <SubscriptionBenefitRow
                                key={perk}
                                benefit={perk}
                                textColor="$peachDarkest"
                                backgroundColor="$peachLight"
                                icon={CirclePlusIcon}
                            />
                        ),
                    )}
                </YStack>
            </YStack>
            <Button
                text="Upgrade"
                variant={ButtonVariant.PREMIUM_SECONDARY}
                size={ButtonSize.LARGE}
                onPress={onPressUpgrade}
            />
        </YStack>
    );
};

const TrialUpgradeOffer = ({
    onPressUpgrade,
}: {
    onPressUpgrade: () => void;
}) => {
    const { value: isPremiumMembershipsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_PREMIUM_MEMBERSHIPS,
    );
    if (!isPremiumMembershipsEnabled) {
        return null;
    }

    return (
        <YStack
            gap={24}
            padding={16}
            borderRadius={16}
            backgroundColor="$peachLightest"
            alignItems="center"
            width="100%"
        >
            <YStack gap={12} width="100%">
                <BodyMediumEmphasized color="$peachDarkest">
                    Upgrade to Premium to also get
                </BodyMediumEmphasized>
                <YStack gap={6} width="100%">
                    {TRIAL_UPGRADE_PERKS.map((perk) => (
                        <SubscriptionBenefitRow
                            key={perk}
                            benefit={perk}
                            textColor="$peachDarkest"
                            backgroundColor="$peachLightest"
                            icon={CirclePlusIcon}
                        />
                    ))}
                </YStack>
            </YStack>
            <Button
                text="Upgrade to Premium"
                variant={ButtonVariant.PREMIUM_SECONDARY}
                size={ButtonSize.LARGE}
                onPress={onPressUpgrade}
            />
        </YStack>
    );
};

const BILLING_FREQUENCY_CADENCE: Record<MembershipBillingFrequency, string> = {
    [MembershipBillingFrequency.MONTHLY]: 'every month',
    [MembershipBillingFrequency.QUARTERLY]: 'every 3 months',
    [MembershipBillingFrequency.YEARLY]: 'every year',
};

const AnnualMembershipSummary = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    const billingFrequency =
        subscription.currentPhase?.billingFrequency ??
        subscription.billingFrequency;
    const billingAmountCents =
        subscription.currentPhase?.billingAmountCents ??
        subscription.amountCents;
    const hasUpcomingPayment =
        subscription.nextInvoiceAmountDueCents != null &&
        subscription.nextInvoiceAmountDueCents > 0;

    return (
        <XStack
            gap={12}
            padding={12}
            borderRadius={16}
            backgroundColor="$peach"
            alignItems="flex-start"
            width="100%"
        >
            <Icon icon={FruitfulLogoIcon} size={24} color="$peachDarkest" />
            <YStack gap={4} flex={1}>
                <BodyMediumEmphasized color="$peachDarkest">
                    Annual membership
                </BodyMediumEmphasized>
                <YStack>
                    {billingFrequency != null && (
                        <BodySmallRegular color="$peachDarkest">
                            Billed at{' '}
                            {centsToFormattedDollars(billingAmountCents, false)}{' '}
                            {BILLING_FREQUENCY_CADENCE[billingFrequency]}
                        </BodySmallRegular>
                    )}
                    {hasUpcomingPayment && (
                        <BodySmallRegular color="$peachDarkest">
                            Next payment due{' '}
                            {format(
                                subscription.renewalDate,
                                SHORT_READABLE_DATE_FORMAT,
                            )}
                        </BodySmallRegular>
                    )}
                </YStack>
            </YStack>
        </XStack>
    );
};

const MembershipStatTile = ({
    label,
    value,
    textColor,
    tileColor,
    isCompactValue = false,
}: {
    label: string;
    value: string;
    textColor: ColorTokenKey;
    tileColor: ColorTokenKey;
    isCompactValue?: boolean;
}) => (
    <YStack
        flex={1}
        flexBasis={0}
        minWidth={0}
        gap={isCompactValue ? 8 : 4}
        padding={12}
        borderRadius={16}
        backgroundColor={tileColor}
        justifyContent={isCompactValue ? 'flex-start' : 'center'}
        overflow="hidden"
    >
        <BodyMediumRegular color={textColor}>{label}</BodyMediumRegular>
        {isCompactValue ? (
            <BodySmallEmphasized color={textColor}>{value}</BodySmallEmphasized>
        ) : (
            <H6Emphasized color={textColor}>{value}</H6Emphasized>
        )}
    </YStack>
);

const MembershipStatGrid = ({
    product,
    textColor,
    tileColor,
}: {
    product: SubscriptionProduct;
    textColor: ColorTokenKey;
    tileColor: ColorTokenKey;
}) => {
    const tileProps = { textColor, tileColor };
    return (
        <YStack gap={8} width="100%">
            <XStack gap={8} width="100%">
                <MembershipStatTile
                    {...tileProps}
                    label="APY¹ on cash"
                    value={product.banking.rateCashAPY}
                />
                <MembershipStatTile
                    {...tileProps}
                    label="Cash back²"
                    value={product.banking.rateCashBack}
                />
            </XStack>
            <XStack gap={8} width="100%">
                <MembershipStatTile
                    {...tileProps}
                    label="AUM³ fee"
                    value={formatAumFeeTileValue(product)}
                    isCompactValue={product.banking.thresholdCentsInvestAUM > 0}
                />
                <MembershipStatTile
                    {...tileProps}
                    label="Guidance"
                    value={
                        product.guidance.includesAssignedGuide ? 'Yes' : 'No'
                    }
                />
            </XStack>
        </YStack>
    );
};

const CancelMembershipSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const sendEmailToSupport = useSendEmailToSupport();

    return (
        <Sheet
            isInsideModal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={true}
        >
            <YStack gap={14}>
                <BodyLargeEmphasized>
                    How can I change or cancel my membership?
                </BodyLargeEmphasized>
                <BodyMediumRegular
                    color="$gray1000"
                    maxWidth={400}
                    alignSelf="center"
                >
                    To change or cancel your Fruitful membership, please contact
                    our Hospitality team at support@fruitful.com. Your request
                    may take 1-2 business days to process.
                </BodyMediumRegular>
                <Button
                    marginTop={8}
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Contact Fruitful Hospitality"
                    onPress={() => {
                        void sendEmailToSupport();
                    }}
                    buttonProps={{
                        testID: 'access-issue-contact-hd-button',
                    }}
                />
            </YStack>
        </Sheet>
    );
};

const AutoRenewCtaAndSheet = ({
    userId,
    subscription,
    copyVariant,
    allowRevert,
}: {
    userId: string;
    subscription: StripeSubscriptionSummary;
    copyVariant: 'premium' | 'legacy';
    allowRevert: boolean;
}) => {
    const { value: isBillingSettingsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const isAutoRenewOff = isSwitchingToStandard(subscription);

    if (
        !isBillingSettingsEnabled ||
        copyVariant === 'legacy' ||
        (isAutoRenewOff && !allowRevert)
    ) {
        return null;
    }

    return (
        <>
            <NavigationRow
                title={
                    isAutoRenewOff
                        ? 'Turn on auto-renew'
                        : 'Turn off auto-renew'
                }
                leftIcon={CalendarIcon}
                containerProps={{
                    borderRadius: 24,
                }}
                onPress={() => {
                    setIsSheetOpen(true);
                }}
            />
            <AutoRenewToggleSheet
                isOpen={isSheetOpen}
                onClose={() => {
                    setIsSheetOpen(false);
                }}
                enabled={isAutoRenewOff}
                copyVariant={copyVariant}
                userId={userId}
                subscription={subscription}
            />
        </>
    );
};

const SubscriptionDetailsLegacy = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary;
}) => {
    const user = useAuthedUser();
    const { value: isBillingSettingsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_BILLING_SETTINGS,
    );
    const benefits =
        subscription.product != null
            ? getSubscriptionBenefits(subscription.product)
            : getLegacySubscriptionBenefits(subscription.type);

    const pendingSwitchDate = subscription.nextPhase?.startDate ?? null;
    const isAutoRenewOff =
        isBillingSettingsEnabled && isSwitchingToStandard(subscription);

    return (
        <>
            <FootnoteEmphasized marginBottom={12}>
                Your membership
            </FootnoteEmphasized>
            <XStack gap={12} alignItems="center">
                <LogoThumbnail />
                <YStack gap={6}>
                    <XStack gap={4}>
                        <BodyMediumRegular>
                            {capitalize(subscription.type ?? '')} Plan -{' '}
                            {capitalize(subscription.billingFrequency ?? '')}
                        </BodyMediumRegular>
                    </XStack>
                    {subscription.cancelAtPeriodEnd &&
                        subscription.cancelAtDate != null && (
                            <LabelRegular>
                                Ends on{' '}
                                {formatReadableMonthDay(
                                    subscription.cancelAtDate,
                                )}{' '}
                            </LabelRegular>
                        )}
                    {!subscription.cancelAtPeriodEnd &&
                        isAutoRenewOff &&
                        pendingSwitchDate != null && (
                            <LabelRegular>
                                Ends on{' '}
                                {formatReadableMonthDay(pendingSwitchDate)}{' '}
                            </LabelRegular>
                        )}
                    {subscription.nextInvoiceAmountDueCents != null && (
                        <LabelRegular>
                            Renews on{' '}
                            {formatReadableMonthDay(subscription.renewalDate)}{' '}
                            for{' '}
                            {formatDollars(
                                centsToDollars(
                                    subscription.nextInvoiceAmountDueCents,
                                ),
                                false,
                            )}
                        </LabelRegular>
                    )}
                </YStack>
            </XStack>

            <FootnoteEmphasized marginBottom={12} marginTop={40}>
                Members get access to
            </FootnoteEmphasized>
            <YStack>
                {benefits.map((benefit, index) => (
                    <SubscriptionBenefitRowLegacy
                        key={index}
                        benefit={benefit}
                        textColor="$gray1200"
                    />
                ))}
                <BenefitsFootnoteLegacy />
            </YStack>
            <YStack marginTop={24} width="100%">
                <AutoRenewCtaAndSheet
                    userId={user.id}
                    subscription={subscription}
                    copyVariant="legacy"
                    allowRevert={false}
                />
            </YStack>
        </>
    );
};

const LogoThumbnail = () => {
    return (
        <View
            backgroundColor="$peach400"
            paddingVertical={8}
            paddingHorizontal={12}
            borderRadius={6}
        >
            <Logo height={30} />
        </View>
    );
};

interface SubscriptionBenefitProps {
    benefit: string;
    textColor: ColorTokenKey;
}

const SubscriptionBenefitRow = ({
    benefit,
    textColor,
    icon,
    backgroundColor,
}: SubscriptionBenefitProps & {
    icon: IconType;
    backgroundColor: ColorTokenKey;
}) => {
    return (
        <XStack alignItems="flex-start" gap={4} width="100%">
            <View
                width={32}
                height={32}
                alignItems="center"
                justifyContent="center"
            >
                <Icon
                    icon={icon}
                    size={24}
                    fill={textColor}
                    color={backgroundColor}
                />
            </View>
            <BodyMediumRegular flex={1} color={textColor} marginTop={4}>
                {benefit}
            </BodyMediumRegular>
        </XStack>
    );
};

const SubscriptionBenefitRowLegacy = ({
    benefit,
    textColor,
}: SubscriptionBenefitProps) => {
    return (
        <XStack alignItems="flex-start" marginVertical={10} gap={10}>
            <Icon icon={CheckCircleIcon} size={20} color={textColor} />
            <BodyMediumRegular maxWidth="85%" color={textColor}>
                {benefit}
            </BodyMediumRegular>
        </XStack>
    );
};

const BenefitsFootnoteLegacy = () => {
    return (
        <XStack alignItems="flex-start" marginTop={24} marginLeft={36}>
            <FootnoteRegular color="$gray1000" maxWidth="90%">
                * See more about rates and other important notes at{' '}
                <Link href="https://www.fruitful.com" target="_blank">
                    <FootnoteRegular
                        textDecorationLine="underline"
                        color="$green1200"
                    >
                        fruitful.com
                    </FootnoteRegular>
                </Link>
            </FootnoteRegular>
        </XStack>
    );
};

export const BenefitsDisclosures = () => {
    return (
        <YStack gap={16} paddingHorizontal={2}>
            <BankingGeneralDisclosure color="$gray1000" />
            <CashApyDisclosure
                color="$gray1000"
                titleColor="$gray1100"
                superscript={1}
            />
            <CashBackDisclosure
                color="$gray1000"
                titleColor="$gray1100"
                superscript={2}
            />
            <InvestmentManagementAgreementDisclosure
                color="$gray1000"
                titleColor="$gray1100"
                superscript={3}
            />
        </YStack>
    );
};

const BenefitsDisclosuresLegacy = () => {
    return (
        <YStack
            gap={12}
            paddingBottom={isNonMobileWeb ? 0 : 48}
            paddingTop={isNonMobileWeb ? 16 : 0}
            paddingHorizontal={2}
        >
            <BankingGeneralDisclosure color="$gray1000" />
            <CashApyDisclosure
                color="$gray1000"
                titleColor="$gray1100"
                superscript={1}
            />
            <CashBackDisclosure
                color="$gray1000"
                titleColor="$gray1100"
                superscript={2}
            />
            <YStack>
                <LabelEmphasized color="$gray1000" lineHeight={16}>
                    Investment Management
                </LabelEmphasized>
                <LabelRegular color="$gray900">
                    “Fruitful” refers to Fruitful, Inc. and its wholly-owned,
                    affiliated, and separately managed subsidiaries, Fruitful
                    Financial, LLC and Fruitful Advisory, LLC, an SEC-registered
                    investment adviser. All investing involves risk, including
                    the risk of losing the money you invest, and past
                    performance does not guarantee future performance.
                    Additional disclosures can be referenced at fruitful.com.
                </LabelRegular>
            </YStack>
            <YStack>
                <LabelRegular color="$gray900">
                    {INVESTMENT_NOT_FDIC_INSURED}
                </LabelRegular>
            </YStack>
        </YStack>
    );
};

interface SubscriptionDetailsProps {
    subscription: StripeSubscriptionSummary;
}

type SubscriptionDetailsComponent = (
    props: SubscriptionDetailsProps,
) => React.JSX.Element;

const SUBSCRIPTION_DETAILS_BY_TYPE: Partial<
    Record<SubscriptionProductId, SubscriptionDetailsComponent>
> = {
    [SubscriptionProductId.SOLO]: SubscriptionDetailsLegacy,
    [SubscriptionProductId.JOINT]: SubscriptionDetailsLegacy,
    [SubscriptionProductId.PLUS]: SubscriptionDetailsPlusEssential,
    [SubscriptionProductId.ESSENTIAL]: SubscriptionDetailsPlusEssential,
    [SubscriptionProductId.TRIAL_PREMIUM]: SubscriptionDetailsCurrent,
    [SubscriptionProductId.PREMIUM]: SubscriptionDetailsCurrent,
    [SubscriptionProductId.STANDARD]: SubscriptionDetailsCurrent,
};

const SubscriptionDetails = ({
    subscription,
}: {
    subscription: StripeSubscriptionSummary | null | undefined;
}) => {
    const product = subscription?.product;
    const productId = product?.id ?? subscription?.type;

    if (subscription == null || productId == null) {
        return <NoMatchingSubscription />;
    }

    const Details = SUBSCRIPTION_DETAILS_BY_TYPE[productId];
    if (Details == null) {
        return <NoMatchingSubscription />;
    }

    return <Details subscription={subscription} />;
};

const NoMatchingSubscription = () => {
    return (
        <>
            <FootnoteEmphasized marginBottom={12}>
                Your membership
            </FootnoteEmphasized>
            <BodyMediumRegular>
                You don&apos;t have a valid subscription. Please reach out to{' '}
                {SUPPORT_EMAIL}.
            </BodyMediumRegular>
        </>
    );
};

export default SubscriptionSettingsScreen;
